import { useMemo, useState, useCallback } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { MODULE_REGISTRY } from '@/constants/moduleRegistry';
import AccessibleLiveRegion from '@/components/common/AccessibleLiveRegion';
import styles from './ModuleDashboardPage.module.css';

// ---------------------------------------------------------------------------
// Logo icon
// ---------------------------------------------------------------------------
function MangoGridIcon(): JSX.Element {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <polygon
        points="24,4 42,14 42,34 24,44 6,34 6,14"
        stroke="#f59e0b"
        strokeWidth="2.5"
        fill="rgba(245,158,11,0.12)"
      />
      <line x1="24" y1="4" x2="24" y2="44" stroke="#f59e0b" strokeWidth="1.5" strokeOpacity="0.55" />
      <line x1="6" y1="14" x2="42" y2="34" stroke="#f59e0b" strokeWidth="1.5" strokeOpacity="0.55" />
      <line x1="6" y1="34" x2="42" y2="14" stroke="#f59e0b" strokeWidth="1.5" strokeOpacity="0.55" />
      <circle cx="24" cy="24" r="4" fill="#f59e0b" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Module icons — one per module id
// ---------------------------------------------------------------------------
function IconDataLab(): JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
      stroke="#0ea5e9" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
    </svg>
  );
}

function IconTradingLab(): JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
      stroke="#10b981" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
      <polyline points="16 7 22 7 22 13" />
    </svg>
  );
}

function IconModelTuner(): JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
      stroke="#f59e0b" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="4"  y1="21" x2="4"  y2="14" />
      <line x1="4"  y1="10" x2="4"  y2="3"  />
      <line x1="12" y1="21" x2="12" y2="12" />
      <line x1="12" y1="8"  x2="12" y2="3"  />
      <line x1="20" y1="21" x2="20" y2="16" />
      <line x1="20" y1="12" x2="20" y2="3"  />
      <line x1="1"  y1="14" x2="7"  y2="14" />
      <line x1="9"  y1="8"  x2="15" y2="8"  />
      <line x1="17" y1="16" x2="23" y2="16" />
    </svg>
  );
}

function IconPowerFlow(): JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
      stroke="#38bdf8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="2,12 5,9 8,15 11,6 14,18 17,9 20,12 22,12" />
    </svg>
  );
}

function IconContingency(): JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
      stroke="#ef4444" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <line x1="12" y1="8"  x2="12"   y2="12"  />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function IconMarketSim(): JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
      stroke="#a78bfa" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
}

function IconRenewables(): JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
      stroke="#34d399" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="5" />
      <line x1="12"   y1="1"     x2="12"   y2="3"     />
      <line x1="12"   y1="21"    x2="12"   y2="23"    />
      <line x1="4.22" y1="4.22"  x2="5.64" y2="5.64"  />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1"    y1="12"    x2="3"    y2="12"    />
      <line x1="21"   y1="12"    x2="23"   y2="12"    />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );
}

function IconModelBuilder(): JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
      stroke="#8b5cf6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14" />
    </svg>
  );
}

function IconConstraintMap(): JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
      stroke="#06b6d4" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <polygon points="3,6 9,3 15,6 21,3 21,18 15,21 9,18 3,21" />
      <line x1="9"  y1="3"  x2="9"  y2="18" />
      <line x1="15" y1="6"  x2="15" y2="21" />
    </svg>
  );
}

const MODULE_ICON_MAP: Record<string, JSX.Element> = {
  'data-lab':       <IconDataLab />,
  'trading-lab':    <IconTradingLab />,
  'model-tuner':    <IconModelTuner />,
  'power-flow':     <IconPowerFlow />,
  'contingency':    <IconContingency />,
  'market-sim':     <IconMarketSim />,
  'renewables':     <IconRenewables />,
  'model-builder':  <IconModelBuilder />,
  'constraint-map': <IconConstraintMap />,
};

// ---------------------------------------------------------------------------
// Utility icons
// ---------------------------------------------------------------------------
function ChevronRightIcon(): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function SearchIcon(): JSX.Element {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function ModuleDashboardPage(): JSX.Element {
  const [search, setSearch] = useState('');
  const [announcement, setAnnouncement] = useState<string | null>(null);

  const modules = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return MODULE_REGISTRY;
    return MODULE_REGISTRY.filter(
      (m) =>
        m.name.toLowerCase().includes(query) ||
        m.description.toLowerCase().includes(query),
    );
  }, [search]);

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearch(value);
      if (!value.trim()) {
        setAnnouncement(`Showing all ${MODULE_REGISTRY.length} modules`);
      } else {
        const count = MODULE_REGISTRY.filter(
          (m) =>
            m.name.toLowerCase().includes(value.trim().toLowerCase()) ||
            m.description.toLowerCase().includes(value.trim().toLowerCase()),
        ).length;
        setAnnouncement(`${count} module${count !== 1 ? 's' : ''} found`);
      }
    },
    [],
  );

  return (
    <div className={styles.page}>
      <AccessibleLiveRegion message={announcement} assertive={false} />

      {/* ── Top navigation bar ── */}
      <nav className={styles.topNav} aria-label="Main navigation">
        <Link to="/dashboard" className={styles.navLogo} aria-label="MangoGridStudio home">
          <MangoGridIcon />
          <span className={styles.navWordmark}>MangoGridStudio</span>
        </Link>

        <span className={styles.navSep} aria-hidden="true" />

        <div className={styles.navLinks}>
          <NavLink
            to="/dashboard"
            className={({ isActive }) =>
              `${styles.navLink}${isActive ? ` ${styles.navLinkActive}` : ''}`
            }
          >
            Dashboard
          </NavLink>
          <NavLink
            to="/modules/constraint-map"
            className={({ isActive }) =>
              `${styles.navLink}${isActive ? ` ${styles.navLinkActive}` : ''}`
            }
          >
            Network Map
          </NavLink>
          <NavLink
            to="/twin"
            className={({ isActive }) =>
              `${styles.navLink}${isActive ? ` ${styles.navLinkActive}` : ''}`
            }
          >
            Grid Twin
          </NavLink>
          <NavLink
            to="/congestion"
            className={({ isActive }) =>
              `${styles.navLink}${isActive ? ` ${styles.navLinkActive}` : ''}`
            }
          >
            Congestion
          </NavLink>
        </div>
      </nav>

      {/* ── Main content ── */}
      <div className={styles.content}>
        <header className={styles.pageHeader}>
          <div>
            <h1 className={styles.pageTitle}>Module Dashboard</h1>
            <p className={styles.pageSubtitle}>
              {MODULE_REGISTRY.length} modules available — select one to begin your analysis
            </p>
          </div>

          <div className={styles.searchWrap}>
            <span className={styles.searchIcon}>
              <SearchIcon />
            </span>
            <input
              id="module-search"
              type="search"
              className={styles.searchInput}
              placeholder="Search modules..."
              data-testid="module-search-input"
              value={search}
              onChange={handleSearch}
              aria-label="Search available modules"
              aria-describedby="search-hint"
            />
            <span id="search-hint" className="sr-only">
              Type to search modules. Results update as you type.
            </span>
          </div>
        </header>

        <section data-testid="module-list" aria-label="Available modules">
          {modules.length > 0 && (
            <p className="sr-only">
              Displaying {modules.length} of {MODULE_REGISTRY.length} modules
            </p>
          )}

          <div className={styles.moduleGrid}>
            {modules.length === 0 && (
              <div className={styles.emptyState} role="status" aria-live="polite">
                <p className={styles.emptyTitle}>No modules match your search</p>
                <p className={styles.emptyDesc}>Try a different keyword</p>
              </div>
            )}

            {modules.map((module) => (
              <Link
                key={module.id}
                to={module.route}
                className={styles.moduleCard}
                data-testid={`module-card-${module.id}`}
                aria-label={`${module.name}: ${module.description}`}
              >
                <div className={styles.moduleIconWrap} aria-hidden="true">
                  {MODULE_ICON_MAP[module.id] ?? <IconModelBuilder />}
                </div>
                <h2 className={styles.moduleCardName} data-testid="module-name">
                  {module.name}
                </h2>
                <p className={styles.moduleCardDesc}>{module.description}</p>
                <div className={styles.moduleOpenRow} aria-hidden="true">
                  Open module <ChevronRightIcon />
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>

      {/* ── Status bar ── */}
      <footer className={styles.statusBar} aria-label="Platform status">
        <span className={styles.statusItem}>
          <span className={styles.statusDot} aria-hidden="true" />
          Solver online
        </span>
        <span className={styles.statusItem}>
          {MODULE_REGISTRY.length} modules loaded
        </span>
      </footer>
    </div>
  );
}
